import { Fragment, PropsWithChildren, useContext, useEffect, useState } from 'react'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import {
    Bars3Icon,
    LinkIcon,
    TagIcon,
    XMarkIcon,
    ArchiveBoxIcon,
    NoSymbolIcon,
    DocumentTextIcon,
    PuzzlePieceIcon,
    ChevronRightIcon,
    FolderIcon,
    ChatBubbleLeftRightIcon,
    TrophyIcon,
    Square3Stack3DIcon,
    Square2StackIcon,
    NewspaperIcon,
    KeyIcon,
    BuildingStorefrontIcon,
    CalendarDaysIcon
} from '@heroicons/react/24/outline'

import {
    DocumentMagnifyingGlassIcon as SolidDocumentMagnifyingGlassIcon
} from '@heroicons/react/24/solid'

import { Link } from 'react-router-dom'
import { classNames } from '../../style/common'
import { useSelector } from 'react-redux'
import { RootState, store } from '../../store'
import { clearDisplayState } from '../../components/ItemDisplay/itemDisplaySlice'
import { NavigationSection, NavigationGroup, NavigationItem } from './types'
import { NavigationContext } from '../../components/Navigation/NavigationContext'
import { UserState } from '../../state/userSlice'
import { ProfileButton } from '../../components/Login/ProfileButton'
import { UserRole } from '../../models/Admin'
import { SiteCostTracker } from '../../components/SiteCostTracker/SiteCostTracker'


export default function Navigation() {
    const {
        isNavOpen,
        setIsNavOpen
    } = useContext(NavigationContext);

    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

    return (
        <>
            <Transition show={isMobileNavOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50 lg:hidden navbar" onClose={setIsMobileNavOpen}>
                    <TransitionChild
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-900/80" />
                    </TransitionChild>

                    <div className="fixed inset-0 flex">
                        <TransitionChild
                            as={Fragment}
                            enter="transition ease-in-out duration-300 transform"
                            enterFrom="-translate-x-full"
                            enterTo="translate-x-0"
                            leave="transition ease-in-out duration-300 transform"
                            leaveFrom="translate-x-0"
                            leaveTo="-translate-x-full"
                        >
                            <DialogPanel className="relative mr-16 flex w-full max-w-xs flex-1">
                                <TransitionChild
                                    as={Fragment}
                                    enter="ease-in-out duration-300"
                                    enterFrom="opacity-0"
                                    enterTo="opacity-100"
                                    leave="ease-in-out duration-300"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                >
                                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                                        <button type="button" className="-m-2.5 p-2.5" onClick={() => setIsMobileNavOpen(false)}>
                                            <span className="sr-only">Close sidebar</span>
                                            <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                                        </button>
                                    </div>
                                </TransitionChild>
                                {/* Sidebar component, swap this element with another sidebar if you like */}
                                <div className="theme-dark flex grow flex-col overflow-y-auto px-6 pb-2 ring-1 ring-white/10 scrollbar scrollbar-track-primary scrollbar-thumb-contrast-tertiary border-r border-r-gray-700 bg-quarternary">
                                    <div className="flex h-10 mt-6 shrink-0 items-center">
                                        <Logo onLinkClick={() => setIsMobileNavOpen(false)} />
                                    </div>
                                    <nav className="flex flex-1 flex-col">
                                        <NavigationLinks onLinkClick={() => setIsMobileNavOpen(false)} />
                                    </nav>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </Dialog>
            </Transition>

            {/* Static sidebar for desktop */}
            <div className={
                classNames(
                    "hidden theme-dark lg:fixed lg:top-14 lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col border-r border-r-gray-700 bg-quarternary",
                    isNavOpen ? "lg:block" : "lg:hidden"
                )}>
                {/* Sidebar component, swap this element with another sidebar if you like */}
                <div className="flex grow flex-col gap-y-2 overflow-y-auto px-6 scrollbar scrollbar-track-quarternary scrollbar-thumb-contrast-tertiary">
                    <nav className="flex flex-1 flex-col">
                        <NavigationLinks onLinkClick={() => setIsMobileNavOpen(false)} />
                    </nav>
                </div>
            </div>

            <div className="flex lg:hidden theme-dark fixed navbar top-0 left-0 right-0 z-50 items-center gap-x-6 bg-quarternary px-4 py-2 shadow-sm sm:px-6 border-b border-color-border">
                <button type="button" className="-m-2.5 p-2.5 text-contrast-tertiary pr-4 sm:pr-4 border-r border-r-gray-700" onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}>
                    <span className="sr-only">Open sidebar</span>
                    <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                </button>
                <Logo onLinkClick={() => setIsMobileNavOpen(false)} />
            </div>

            <div className="hidden lg:flex theme-dark fixed navbar top-0 left-0 right-0 z-50 items-center gap-x-6 bg-quarternary px-4 py-2 shadow-sm sm:px-6 border-b border-color-border">
                <button type="button" className="-m-2.5 p-2.5 text-contrast-tertiary pr-4 sm:pr-4 border-r border-r-gray-700" onClick={() => setIsNavOpen(!isNavOpen)}>
                    <span className="sr-only">Open sidebar</span>
                    <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                </button>
                <Logo onLinkClick={() => setIsMobileNavOpen(false)} />
            </div>
        </>
    )
}

function Logo(props: { onLinkClick: () => void }) {
    return (
        <Link to="/" className='flex items-center gap-2 h-10' onClick={props.onLinkClick}>
            <img
                className="w-auto select-none h-8"
                src={`/logo.png`}
                alt="materia hunter logo"
            />

            <img src="/logo-text.svg" className='w-40 mt-1' />
        </Link>
    )
}

function NavigationLinks(props: { onLinkClick: () => void }) {
    const {
        isLoggedIn,
        isAdmin,
        isContributor
    } = useSelector<RootState, UserState>(x => x.user);

    const [openState, setOpenState] = useState<Record<string, boolean>>({});

    useEffect(() => {
        const newOpenState: Record<string, boolean> = {};
        for (const section of navigation) {
            for (const item of section.items) {
                const group: NavigationGroup = item as NavigationGroup;
                if (!group.children) {
                    continue;
                }

                const isOpenString = localStorage.getItem(group.storageKey);
                const isOpen = (isOpenString === "true");
                newOpenState[group.storageKey] = isOpen;
            }
        }

        for (const section of myContentNavigation) {
            for (const item of section.items) {
                const group: NavigationGroup = item as NavigationGroup;
                if (!group.children) {
                    continue;
                }

                const isOpenString = localStorage.getItem(group.storageKey);
                const isOpen = (isOpenString === "true");
                newOpenState[group.storageKey] = isOpen;
            }
        }

        setOpenState(newOpenState);
    }, []);

    function updateOpenState(key: string, value: boolean) {
        const copy: Record<string, boolean> = JSON.parse(JSON.stringify(openState));
        copy[key] = value;
        setOpenState(copy);
    }

    function showItem(roles: UserRole[] | undefined): boolean {
        if (!roles) {
            return true
        }

        if (roles.includes("admin") && isAdmin) {
            return true
        }

        if (roles.includes("contributor") && isContributor) {
            return true
        }

        return false
    }

    return (
        <ul role="list" className="flex flex-1 flex-col pt-4 mb-10">
            <li className='mb-6'>
                <SiteCostTracker />
            </li>
            <li>
                <ul role="list" className="-mx-2">
                    <ProfileButton onLinkClick={props.onLinkClick} />
                </ul>
            </li>

            {
                isLoggedIn &&
                <>
                    {
                        myContentNavigation.map((section, index) => <NavSectionComponent
                            key={`${index}-a`}
                            isLoggedIn={isLoggedIn}
                            onLinkClick={props.onLinkClick}
                            openState={openState}
                            section={section}
                            showItem={showItem}
                            updateOpenState={updateOpenState} />
                        )
                    }

                    <li className='border-b border-b-color-border/75 my-4'></li>
                </>
            }
            {
                navigation.map((section, index) => <NavSectionComponent
                    key={`${index}-b`}
                    isLoggedIn={isLoggedIn}
                    onLinkClick={props.onLinkClick}
                    openState={openState}
                    section={section}
                    showItem={showItem}
                    updateOpenState={updateOpenState} />
                )
            }
        </ul>
    )
}

type NavSectionProps = {
    section: NavigationSection,
    isLoggedIn: boolean,
    openState: Record<string, boolean>,
    onLinkClick: () => void,
    showItem(roles: UserRole[] | undefined): boolean,
    updateOpenState(key: string, value: boolean): void
}

function NavSectionComponent(props: NavSectionProps) {
    return (
        <li>
            {
                props.section.name &&
                <div className="text-xs font-semibold leading-6 text-contrast-tertiary">{props.section.name}</div>
            }
            <ul role="list" className="-mx-2">
                {props.section.items.map((item, innerIndex) => {
                    const group: NavigationGroup = item as NavigationGroup;
                    if (group.loginRequired && !props.isLoggedIn) {
                        return undefined;
                    }

                    if (!props.showItem(group.userRoles)) {
                        return undefined;
                    }

                    if (group.children) {
                        return (
                            <NavGroup
                                key={`${innerIndex}`}
                                group={group}
                                isLoggedIn={props.isLoggedIn}
                                isOpen={props.openState[group.storageKey]}
                                onGroupTitleClick={() => {
                                    localStorage.setItem(group.storageKey, `${!props.openState[group.storageKey]}`);
                                    props.updateOpenState(group.storageKey, !props.openState[group.storageKey]);
                                }}
                                onNavItemClick={props.onLinkClick}
                                showItem={props.showItem} />
                        )
                    } else {
                        const navItem: NavigationItem = item as NavigationItem;
                        if (navItem.loginRequired && !props.isLoggedIn) {
                            return undefined;
                        }

                        if (!props.showItem(navItem.userRoles)) {
                            return undefined;
                        }

                        return (
                            <NavItem
                                key={`${innerIndex}`}
                                to={navItem.href}
                                onClick={() => {
                                    if (navItem.onClick) {
                                        navItem.onClick();
                                    }

                                    props.onLinkClick();
                                }}>
                                <navItem.icon className="h-6 w-6 shrink-0" aria-hidden="true" />
                                {navItem.name}
                            </NavItem>
                        )
                    }
                })}
            </ul>
        </li>
    )
}

function NavItem(props: {
    href?: string,
    to?: string,
    onClick?: () => void
} & PropsWithChildren) {
    const style = "fill-contrast-tertiary text-contrast-tertiary hover:bg-emphasis hover:text-emphasis-contrast hover:fill-emphasis-contrast group rounded-md p-2 text-sm leading-6 font-semibold flex gap-x-3 select-none";

    return (
        <li onClick={props.onClick} draggable={false}>
            {
                props.href &&
                <a href={props.href} target='_blank' className={style} draggable={false}>
                    {props.children}
                </a>
            }
            {
                props.to &&
                <Link to={props.to} className={style} draggable={false}>
                    {props.children}
                </Link>
            }
        </li>
    )
}

type NavGroupProps = {
    group: NavigationGroup,
    isOpen: boolean,
    isLoggedIn: boolean,
    onGroupTitleClick: () => void,
    onNavItemClick: () => void,
    showItem: (roles: UserRole[] | undefined) => boolean
}

function NavGroup(props: NavGroupProps) {
    return (
        <div>
            <div
                onClick={props.onGroupTitleClick}
                className='cursor-pointer flex items-center justify-between w-full text-left rounded-md p-2 gap-x-3 text-sm leading-6 font-semibold fill-contrast-tertiary text-contrast-tertiary hover:bg-emphasis hover:text-emphasis-contrast hover:fill-emphasis-contrast select-none'
            >
                <div className='flex space-x-3'>
                    <props.group.icon className="h-6 w-6 shrink-0" aria-hidden="true" />
                    <span>{props.group.name}</span>
                </div>
                <ChevronRightIcon
                    className={classNames(
                        props.isOpen ? 'rotate-90' : '',
                        'h-6 w-6 shrink-0'
                    )}
                    aria-hidden="true"
                />
            </div>
            <ul className={classNames("mt-1 pr-2", props.isOpen ? "block" : "hidden")}>
                {props.group.children.map((subItem, childIndex) => {
                    if (subItem.loginRequired && !props.isLoggedIn) {
                        return undefined;
                    }

                    if (!props.showItem(subItem.userRoles)) {
                        return undefined;
                    }

                    return (
                        <NavItem
                            key={`${childIndex}`}
                            to={subItem.href}
                            onClick={() => {
                                if (subItem.onClick) {
                                    subItem.onClick();
                                }

                                props.onNavItemClick();
                            }}>
                            <span className='pl-9 font-normal'>{subItem.name}</span>
                        </NavItem>
                    )
                })}
            </ul>
        </div>
    )
}

const myContentNavigation: NavigationSection[] = [
    {
        items: [
            {
                name: "My Content",
                icon: Square2StackIcon,
                storageKey: "materiahunter-nav-mycontent-isOpen",
                children: [
                    {
                        name: 'Decks',
                        href: '/deck-list/my',
                        icon: FolderIcon
                    },
                    {
                        name: 'Tournaments',
                        href: '/tournaments/edit',
                        icon: FolderIcon,
                        onClick: () => {
                            window.scrollTo(0, 0);
                        }
                    },
                    {
                        name: 'Collection',
                        href: '/cards/collection',
                        icon: ArchiveBoxIcon,
                        onClick: () => {
                            store.dispatch(clearDisplayState());
                            window.scrollTo(0, 0);
                        }
                    },
                    {
                        name: 'Trades',
                        href: '/trade-lists',
                        icon: ArchiveBoxIcon,
                        onClick: () => {
                            store.dispatch(clearDisplayState());
                            window.scrollTo(0, 0);
                        }
                    }
                ]
            }
        ]
    }
];

const navigation: NavigationSection[] = [
    {
        items: [
            {
                name: 'News',
                href: '/news',
                icon: NewspaperIcon,
                onClick: () => {
                    store.dispatch(clearDisplayState());
                    window.scrollTo(0, 0);
                }
            },
            {
                name: 'Cards',
                href: '/cards',
                icon: Square2StackIcon,
                onClick: () => {
                    store.dispatch(clearDisplayState());
                    window.scrollTo(0, 0);
                }
            },
            {
                name: 'Decks',
                href: '/deck-list/all',
                icon: Square3Stack3DIcon
            },
            {
                name: 'Events',
                href: '/events',
                icon: CalendarDaysIcon,
                onClick: () => {
                    store.dispatch(clearDisplayState());
                    window.scrollTo(0, 0);
                }
            },
            {
                name: 'Tournaments',
                href: '/tournaments',
                icon: TrophyIcon,
                onClick: () => {
                    window.scrollTo(0, 0);
                }
            },
            {
                name: 'Venues',
                href: '/venues',
                icon: BuildingStorefrontIcon,
                onClick: () => {
                    window.scrollTo(0, 0);
                }
            },
            {
                name: 'Products',
                href: '/products',
                icon: TagIcon,
                onClick: () => {
                    store.dispatch(clearDisplayState());
                    window.scrollTo(0, 0);
                }
            },
            {
                name: 'Links',
                href: '/links',
                icon: LinkIcon,
                onClick: () => {
                    store.dispatch(clearDisplayState());
                    window.scrollTo(0, 0);
                }
            }
        ]
    },

    {
        items: [
            {
                name: "Rules",
                icon: SolidDocumentMagnifyingGlassIcon,
                storageKey: "materiahunter-nav-rules-isOpen",
                children: [
                    {
                        name: "Formats / Ban List",
                        href: "/game/formats",
                        icon: NoSymbolIcon,
                        onClick: () => {
                            store.dispatch(clearDisplayState());
                            window.scrollTo({ top: 0, behavior: "smooth" });
                        }
                    },
                    {
                        name: "Errata",
                        href: "/cards/errata",
                        icon: DocumentTextIcon,
                        onClick: () => {
                            store.dispatch(clearDisplayState());
                            window.scrollTo({ top: 0, behavior: "smooth" });
                        }
                    },
                    { name: 'Game Phases', href: '/phases', icon: PuzzlePieceIcon },
                    { name: 'Rules FAQ', href: '/faq', icon: ChatBubbleLeftRightIcon }
                ]
            },
        ]
    },

    {
        items: [
            {
                name: "Admin",
                loginRequired: true,
                icon: KeyIcon,
                storageKey: "materiahunter-nav-admin-isOpen",
                userRoles: ["admin", "contributor"],
                children: [
                    {
                        name: 'Misc',
                        href: '/admin',
                        icon: ArchiveBoxIcon,
                        onClick: () => {
                            store.dispatch(clearDisplayState());
                            window.scrollTo(0, 0);
                        },
                        userRoles: ["admin"]
                    },
                    {
                        name: 'Archetypes',
                        href: '/admin/archetypes',
                        icon: ArchiveBoxIcon,
                        onClick: () => {
                            store.dispatch(clearDisplayState());
                            window.scrollTo(0, 0);
                        },
                        userRoles: ["admin"]
                    },
                    {
                        name: 'Cards',
                        href: '/admin/cards',
                        icon: ArchiveBoxIcon,
                        onClick: () => {
                            store.dispatch(clearDisplayState());
                            window.scrollTo(0, 0);
                        },
                        userRoles: ["admin", "contributor"]
                    },
                    {
                        name: 'Products',
                        href: '/admin/products',
                        icon: ArchiveBoxIcon,
                        onClick: () => {
                            store.dispatch(clearDisplayState());
                            window.scrollTo(0, 0);
                        },
                        userRoles: ["admin"]
                    },
                    {
                        name: 'News',
                        href: '/news/all',
                        icon: ArchiveBoxIcon,
                        onClick: () => {
                            store.dispatch(clearDisplayState());
                            window.scrollTo(0, 0);
                        },
                        userRoles: ["admin"]
                    },
                    {
                        name: 'Tournaments',
                        href: '/admin/tournaments',
                        icon: ArchiveBoxIcon,
                        onClick: () => {
                            store.dispatch(clearDisplayState());
                            window.scrollTo(0, 0);
                        },
                        userRoles: ["admin", "contributor"]
                    },
                    {
                        name: 'Rules',
                        href: '/admin/rules',
                        icon: ArchiveBoxIcon,
                        onClick: () => {
                            store.dispatch(clearDisplayState());
                            window.scrollTo(0, 0);
                        },
                        userRoles: ["admin", "contributor"]
                    }
                ]
            }
        ]
    }
];